import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const NewsView = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchNews = async () => {
      try {
        const response = await fetch(
          `/api/get_news_by_id.php?id=${id}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (data.success && isMounted) {
          setNews(data.news);
        }
      } catch (err) {
        console.error("Ошибка при загрузке новости:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchNews();

    return () => (isMounted = false);
  }, [id]);

  useEffect(() => {
    if (!news?.id) return;

    const fetchRelated = async () => {
      try {
        const res = await fetch(
          `/api/get_related_news.php?id=${news.id}`
        );
        const data = await res.json();
        if (data.success) setRelatedNews(data.related);
      } catch (err) {
        console.error("Ошибка при загрузке связанных новостей:", err);
      }
    };

    fetchRelated();
  }, [news]);

  const imageUrls = useMemo(
    () => ({
      background: news?.image
        ? `/api/uploads/${news.image}`
        : "https://img.freepik.com/free-photo/beautiful-landscape-mountains_23-2148466479.jpg",
      companyLogo: news?.companyLogo
        ? `/api/uploads/${news.companyLogo}`
        : "https://img.freepik.com/free-vector/bird-colorful-logo-gradient-vector_343694-1365.jpg",
    }),
    [news]
  );

  if (loading) {
    return (
      <div className="w-full h-[300px] flex justify-center items-center">
        <p className="text-white text-xl font-semibold animate-pulse">
         {t("loading")}...
        </p>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="text-center text-red-500 mt-10">News not found.</div>
    );
  }

  return (
    <section className="max-w-7xl mx-auto opacity-0 animate-[show_1s_forwards] h-max bg-white flex flex-col gap-3 transition-opacity duration-700">
      {/* Background image */}
      <div className="relative w-full h-[300px] max-sm:h-[200px] mb-20 border-b-4 border-[#006674]">
        <img
          src={imageUrls.background}
          loading="lazy"
          className="w-full h-full object-cover"
          alt="Background"
        />
        <img
          src={imageUrls.companyLogo}
          loading="lazy"
          className="w-[140px] h-[140px] max-sm:w-[120px] max-sm:h-[120px] absolute top-56 max-sm:top-36 left-6 object-contain rounded-xl shadow-2xl bg-white"
          alt={news.companyName || "Company"}
        />
      </div>

      {/* Заголовок и данные */}
      <div className="px-6 pb-6 flex flex-col gap-3 max-sm:px-2">
        <p className="text-[#006674] text-2xl">{news.companyName}</p>
        <h1 className="text-3xl max-md:text-2xl font-semibold">{news.title}</h1>
        <div className="text-gray-500 w-full flex justify-between items-center">
            <span>ID:{news.id}</span>
        </div>
        <hr className="border-[#006674] my-4" />

        <p className="text-lg max-md:text-[16px] leading-relaxed text-gray-800 whitespace-pre-line">
          {news.description}
        </p>

        <div className="flex justify-between mt-6 max-md:mt-2 items-center gap-3 max-sm:flex-col-reverse max-sm:items-start">
          <Link
            to={`/ViewCompanyPage/${news.user_id}`}
            className="text-white bg-[#006674] py-2 px-3 rounded font-semibold hover:underline"
          >
            {t("viewcompanypage")}
          </Link>
           <span className="text-[#006674] ">{t("created")} {new Date(news.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Похожие новости */}
      {relatedNews.length > 0 && (
        <div className="px-6 pb-10 mt-10">
          <h2 className="text-3xl text-[#006674] font-bold mb-5">
            {t("relatednews") || "Related News"}
          </h2>
          <div className="w-full flex flex-wrap gap-5">
            {relatedNews.map((item) => (
              <article
                key={item.id}
                className="w-[400px] bg-white shadow-2xl rounded-lg overflow-hidden hover:-translate-y-1 transition-transform"
              >
                <img
                  src={`/api/uploads/${item.image}`}
                  alt={item.title}
                  className="w-full h-[200px] object-cover"
                />
                <div className="p-4 flex flex-col gap-2">
                  <h3 className="text-xl font-semibold text-[#006674]">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-3">
                    {item.description}
                  </p>
                  <Link
                    to={`/news/${item.id}`}
                    className="text-[#006674] font-semibold mt-2 hover:underline"
                  >
                    {t("seemore")}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default NewsView;
